import { Column, Table, TableOptions, WorkspaceIcon, CheckIcon } from '@acceso/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TableRow } from '@/components/TableRow';
import { TableRowImage } from '@/components/TableRowImage';
import { TableRowInfo } from '@/components/TableRowInfo/TableRowInfo';
import { useClientsByIds } from '@/features/app/api/get-clients-by_ids';
import { useSearchesByIds } from '@/features/app/api/get-searches-by_ids';
import { useWorkspacesByIds } from '@/features/app/api/get-workspaces_by_ids';

import { TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem } from '../../../types';

type TBroadcastScheduleTableProps = {
  items: TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem[];
  showTentative?: boolean;
};

export function BroadcastScheduleProgramBodySegmentToolkitAditionalInfoTable({
  items,
  showTentative = false,
}: TBroadcastScheduleTableProps) {
  const extractQueryId = (queryId?: string | null) =>
    queryId?.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0] ?? null;
  const normalizedQueryIds = items
    .map(item => extractQueryId(item.queryId))
    .filter((id): id is string => Boolean(id));

  const { t } = useTranslation();
  const { data: workspaces = [] } = useWorkspacesByIds({
    ids: items.map(item => item.workspaceId),
  });
  const { data: clients = [] } = useClientsByIds({
    ids: items.map(item => item.clientId),
  });
  const { data: searches = [] } = useSearchesByIds({
    ids: normalizedQueryIds,
  });
  const [search, setSearch] = useState('');
  const filteredItems =
    search !== ''
      ? items.filter(item => {
          const workspace = workspaces.find(workspace => workspace.id === item.workspaceId);

          return workspace?.name.toLowerCase().includes(search.toLowerCase());
        })
      : items;

  const options: Partial<TableOptions> = {
    initialSortBy: { id: 'updated_at', order: 'desc' },
    searchBarPlaceholder: t('Buscar por asignación'),
    onSearchChange: setSearch,
  };

  const columns: Column<TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem>[] = [
    {
      id: 'asinagcion',
      title: t('Espacio de trabajo'),
      render: item => {
        const workspace = workspaces.find(workspace => workspace.id === item.workspaceId);

        return <TableRowInfo icon={WorkspaceIcon} title={workspace?.name} />;
      },
    },
    {
      id: 'search',
      title: t('Búsqueda'),
      render: item => {
        const queryId = extractQueryId(item.queryId);

        return <TableRow content={searches.find(search => search.id === queryId)?.name ?? ''} />;
      },
    },
    {
      id: 'client',
      title: t('Cliente'),
      render: item => {
        const client = clients.find(client => client.id === item.clientId);

        return (
          <TableRowImage
            name={client?.name}
            source={client?.parameters?.logo_positive}
            alt="Client logo"
          />
        );
      },
    },
    ...(showTentative
      ? ([
          {
            id: 'tentative',
            title: t('Preasignación'),
            render: item => (item.isTentative ? <CheckIcon className="text-extra-70" /> : null),
          } as Column<TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem>,
        ] as Column<TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem>[])
      : []),
  ];

  return <Table options={options} columns={columns} data={filteredItems} ignoreWrap />;
}
